import React, { useState, useEffect } from "react";
import OutsideClickHandler from "react-outside-click-handler";

function isSameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isInRange(day, start, end) {
  if (!start || !end || !day) return false;
  const s = start < end ? start : end;
  const e = start < end ? end : start;
  return day > s && day < e;
}

function CalendarGrid({
  year,
  month,
  startDate,
  endDate,
  hoverDate,
  onDayClick,
  onDayHover,
  t,
}) {
  const today = new Date();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const DAYS_OF_WEEK = [
    t("sunday"),
    t("monday"),
    t("tuesday"),
    t("wednesday"),
    t("thursday"),
    t("friday"),
    t("saturday"),
  ];

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));

  const rangeEnd = endDate || hoverDate;

  return (
    <div style={styles.calGrid}>
      {DAYS_OF_WEEK.map((d) => (
        <div key={d} style={styles.calDow}>
          {d}
        </div>
      ))}
      {cells.map((day, i) => {
        if (!day) return <div key={`e${i}`} />;

        const isStart = isSameDay(day, startDate);
        const isEnd = isSameDay(day, rangeEnd) && endDate;
        const inRange = isInRange(day, startDate, rangeEnd);
        const isToday = isSameDay(day, today);

        const dayStyle = {
          ...styles.calDay,
          ...(inRange ? styles.inRange : {}),
          ...(isStart ? styles.rangeStart : {}),
          ...(isEnd ? styles.rangeEnd : {}),
          ...(isStart && isEnd ? styles.rangeStartEnd : {}),
          ...(isToday && !isStart && !isEnd ? styles.today : {}),
        };

        return (
          <div
            key={day.getDate()}
            style={dayStyle}
            onClick={() => onDayClick(day)}
            onMouseEnter={() => onDayHover(day)}
          >
            {day.getDate()}
          </div>
        );
      })}
    </div>
  );
}

export default function DateRangeModal({ 
  onClose, 
  onConfirm, 
  isOpen = false,
  initialStartDate = new Date(2022, 1, 1),
  initialEndDate = new Date(2023, 0, 1),
  t
}) {
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  // to be replaced with date range picker
//   const today = new Date();

//   const oneMonthAgo = new Date();
//   oneMonthAgo.setMonth(today.getMonth() - 1);

//   const [startDate, setStartDate] = useState(oneMonthAgo);
//   const [endDate, setEndDate] = useState(today);

  const [selecting, setSelecting] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);
  const [activePreset, setActivePreset] = useState(null);

  // Initialize viewYear and viewMonth based on startDate
  const [viewYear, setViewYear] = useState(() => startDate?.getFullYear() || new Date().getFullYear());
  const [viewMonth, setViewMonth] = useState(() => startDate?.getMonth() || new Date().getMonth());
  
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [showMonthPicker, setShowMonthPicker] = useState(false);

  // Update dates when modal opens
  useEffect(() => {
    if (isOpen) {
      setStartDate(initialStartDate);
      setEndDate(initialEndDate);
      setViewYear(initialStartDate?.getFullYear() || new Date().getFullYear());
      setViewMonth(initialStartDate?.getMonth() || new Date().getMonth());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  function handleDayClick(day) {
    if (!selecting || !startDate) {
      setStartDate(day);
      setEndDate(null);
      setSelecting(true);
      setActivePreset(null);
    } else {
      if (day < startDate) {
        setStartDate(day);
        setEndDate(startDate);
      } else {
        setEndDate(day);
      }
      setSelecting(false);
      setHoverDate(null);
    }
  }

  function applyPreset(days) {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days + 1);
    setStartDate(start);
    setEndDate(end);
    setSelecting(false);
    setHoverDate(null);
    setViewYear(end.getFullYear());
    setViewMonth(end.getMonth());
    setActivePreset(days);
  }

  function prevMonth() {
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else setViewMonth((m) => m - 1);
  }

  function nextMonth() {
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else setViewMonth((m) => m + 1);
  }

  function changeMonth(month) {
    setViewMonth(month);
    setShowMonthPicker(false);
  }

  function changeYear(year) {
    setViewYear(year);
    setShowYearPicker(false);
  }

  function handleConfirm() {
    onConfirm && onConfirm({ startDate, endDate });
  }

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  function reset() {
    const today = new Date();
    const oneMonthAgo = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());
    setStartDate(oneMonthAgo);
    setEndDate(today);
    setSelecting(false);
    setHoverDate(null);
    setActivePreset(null);
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
  }

  const presets = [
    { label: t("last 7 days"), days: 7 },
    { label: t("last 30 days"), days: 30 },
    { label: t("last 3 months"), days: 90 },
    { label: t("last 1 year"), days: 365 },
  ];

  return (
    <div style={styles.backdrop}>
      <OutsideClickHandler onOutsideClick={onClose || reset}>
        <div style={styles.modal}>
          {/* Header */}
          <div style={styles.modalHeader}>
            <span style={styles.modalTitle}>{t("select date range")}</span>
            <button style={styles.closeBtn} onClick={onClose || reset}>
              ✕
            </button>
          </div>

          {/* Body */}
          <div style={styles.modalBody}>
            {/* Calendar */}
            <div style={{ flex: 1, minWidth: 0, position: "relative" }}>
              <div style={styles.calNav}>
                <div style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", position: "relative" }}>
                  <span
                    style={styles.calMonth}
                    onClick={() => {
                      setShowMonthPicker(!showMonthPicker);
                      setShowYearPicker(false);
                    }}
                  >
                    {t(["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"][viewMonth])}
                  </span>
                  <span
                    style={styles.calMonth}
                    onClick={() => {
                      setShowYearPicker(!showYearPicker);
                      setShowMonthPicker(false);
                    }}
                  >
                    {viewYear}
                  </span>
                </div>
                <div style={{ display: "flex", gap: 4 }}>
                  <button style={styles.navBtn} onClick={prevMonth}>
                    ‹
                  </button>
                  <button style={styles.navBtn} onClick={nextMonth}>
                    ›
                  </button>
                </div>
              </div>

              {/* Month Picker Dropdown */}
              {showMonthPicker && (
                <>
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 999,
                    }}
                    onClick={() => setShowMonthPicker(false)}
                  />
                  <div style={styles.dropdownMenu}>
                    {["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"].map((monthKey, idx) => (
                      <div
                        key={idx}
                        style={{
                          ...styles.dropdownItem,
                          ...(idx === viewMonth ? styles.dropdownItemActive : {}),
                        }}
                        onClick={() => changeMonth(idx)}
                      >
                        {t(monthKey)}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* Year Picker Dropdown */}
              {showYearPicker && (
                <>
                  <div
                    style={{
                      position: "fixed",
                      inset: 0,
                      zIndex: 999,
                    }}
                    onClick={() => setShowYearPicker(false)}
                  />
                  <div style={styles.dropdownMenu}>
                    {yearOptions.map((year) => (
                      <div
                        key={year}
                        style={{
                          ...styles.dropdownItem,
                          ...(year === viewYear ? styles.dropdownItemActive : {}),
                        }}
                        onClick={() => changeYear(year)}
                      >
                        {year}
                      </div>
                    ))}
                  </div>
                </>
              )}

              <CalendarGrid
                year={viewYear}
                month={viewMonth}
                startDate={startDate}
                endDate={endDate}
                hoverDate={selecting ? hoverDate : null}
                onDayClick={handleDayClick}
                onDayHover={(d) => selecting && setHoverDate(d)}
                t={t}
              />
            </div>

            {/* Presets */}
            <div style={styles.presets}>
              {presets.map((p) => (
                <button
                  key={p.days}
                  style={{
                    ...styles.presetBtn,
                    ...(activePreset === p.days ? styles.presetBtnActive : {}),
                  }}
                  onClick={() => applyPreset(p.days)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Footer */}
          <div style={styles.modalFooter}>
            <div style={styles.maxRangeNote}>{t("Maximum date range")}</div>
            <div style={styles.footerBtns}>
              <button style={styles.btnReset} onClick={reset}>
                {t("reset")}
              </button>
              <button
                style={styles.btnConfirm}
                onClick={handleConfirm}
              >
                {t("Confirm")}
              </button>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
}

const styles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.4)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 10px 40px rgba(0,0,0,0.15)",
    padding: 24,
    width: 520,
    maxWidth: "96vw",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#2E3131",
    letterSpacing: -0.2,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "none",
    background: "#F5F5F5",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    fontSize: 16,
    transition: "all 0.2s",
  },
  modalBody: {
    display: "flex",
    gap: 24,
  },
  calNav: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  calMonth: {
    fontSize: 15,
    fontWeight: 600,
    color: "#2E3131",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: 4,
    transition: "background-color 0.2s",
  },
  navBtn: {
    width: 28,
    height: 28,
    borderRadius: 6,
    border: "1px solid #E8EAED",
    background: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#555",
    fontSize: 16,
    fontWeight: 500,
  },
  dropdownMenu: {
    position: "absolute",
    top: 36,
    left: 0,
    background: "#fff",
    border: "1px solid #E8EAED",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    zIndex: 1001,
    maxHeight: 200,
    overflowY: "auto",
    minWidth: 120,
  },
  dropdownItem: {
    padding: "8px 12px",
    fontSize: 13,
    color: "#2E3131",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  dropdownItemActive: {
    background: "#0179D2",
    color: "#fff",
    fontWeight: 600,
  },
  calGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(7, 1fr)",
    gap: 6,
  },
  calDow: {
    fontSize: 12,
    fontWeight: 600,
    color: "#9ca3af",
    textAlign: "center",
    padding: "6px 0",
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  calDay: {
    aspectRatio: "1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    color: "#2E3131",
    cursor: "pointer",
    borderRadius: 6,
    userSelect: "none",
    fontWeight: 500,
    transition: "all 0.2s",
  },
  inRange: {
    background: "#E3F2FD",
    borderRadius: "100px",
    color: "#0179D2",
    fontWeight: 500,
  },
  rangeStart: {
    background: "#0179D2",
    color: "#fff",
    borderRadius: "100px",
    fontWeight: 600,
  },
  rangeEnd: {
    background: "#0179D2",
    color: "#fff",
    borderRadius: "100px",
    fontWeight: 600,
  },
  rangeStartEnd: {
    borderRadius: 100,
  },
  today: {
    fontWeight: 800,
    color: "#0179D2",
    border: "2px solid #0179D2",
  },
  presets: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 125,
  },
  presetBtn: {
    padding: "10px 12px",
    borderRadius: 6,
    border: "1.5px solid #E8EAED",
    background: "#fff",
    fontSize: 13,
    fontWeight: 500,
    color: "#2E3131",
    cursor: "pointer",
    textAlign: "center",
    whiteSpace: "nowrap",
    transition: "all 0.2s",
  },
  presetBtnActive: {
    borderColor: "#0179D2",
    color: "#0179D2",
    background: "#EBF5FE",
  },
  modalFooter: {
    width: "100%",
    marginTop: 20,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 12,
  },
  maxRangeNote: {
    fontSize: 12,
    color: "#9ca3af",
    textAlign: "center",
  },
  footerBtns: {
    display: "flex",
    gap: 10,
  },
  btnReset: {
    padding: "10px 22px",
    borderRadius: 6,
    border: "1.5px solid #E8EAED",
    background: "#fff",
    fontSize: 14,
    fontWeight: 500,
    color: "#2E3131",
    cursor: "pointer",
    width: "100%",
    transition: "all 0.2s",
  },
  btnConfirm: {
    padding: "10px 28px",
    borderRadius: 6,
    border: "none",
    background: "#0179D2",
    fontSize: 14,
    fontWeight: 600,
    color: "#fff",
    cursor: "pointer",
    width: "100%",
    transition: "all 0.2s",
  },
};
